<script setup lang="ts">
/**
 * OrgLogo — rendu unifié d'un logo d'association.
 *
 * Un logo N'EST PAS une photo : il doit toujours être affiché en ENTIER
 * (`object-contain`), jamais recadré (`object-cover` couperait un logo
 * horizontal type « HelloAsso » et le rendrait illisible). Ce composant
 * centralise ce choix + le fond + le fallback + le resize Directus, pour que
 * TOUS les logos du site soient cohérents et ne se re-divergent pas.
 * (Chantier cadrage images asso 2026-07-31.)
 *
 * Accepte soit un UUID Directus (`logo`), soit une URL absolue (http / /...).
 */
const props = withDefaults(
  defineProps<{
    /** UUID Directus du logo, ou URL absolue, ou null. */
    logo?: string | null;
    /** Nom de l'asso (alt + initiale du fallback). */
    name?: string | null;
    /** Taille CSS (classes tailwind w-/h-). Défaut w-12 h-12. */
    sizeClass?: string;
    /** Arrondi. Défaut rounded-xl. */
    roundedClass?: string;
    /** Largeur demandée à Directus (px). Défaut 200 (retina d'un logo ~64px). */
    width?: number;
    /** Classes de bordure/ombre additionnelles. */
    ringClass?: string;
    /**
     * Logo du PREMIER écran : chargé tout de suite et en priorité haute.
     * `lazy` sur une image déjà visible retarde sa requête après la mise en
     * page. (Audit lenteur images 2026-09-19.)
     */
    priority?: boolean;
  }>(),
  {
    logo: null,
    name: "",
    sizeClass: "w-12 h-12",
    roundedClass: "rounded-xl",
    width: 200,
    ringClass: "border-2 border-white shadow-md",
    priority: false,
  },
);

const config = useRuntimeConfig();
const assetsBaseUrl =
  (config.public.apiUrl as string) || "https://api.donoor.org";

const src = computed<string | null>(() => {
  const l = props.logo;
  if (!l) return null;
  if (l.startsWith("http") || l.startsWith("/")) return l;
  // fit=contain : Directus letterbox le logo dans un carré sans le rogner.
  return `${assetsBaseUrl}/api/assets/${l}?width=${props.width}&height=${props.width}&fit=contain&format=webp&quality=85`;
});

const initial = computed(() =>
  (props.name || "").trim().charAt(0).toUpperCase() || "•",
);
</script>

<template>
  <div
    :class="[
      sizeClass,
      roundedClass,
      ringClass,
      'flex items-center justify-center overflow-hidden bg-white shrink-0',
    ]"
  >
    <img
      v-if="src"
      :src="src"
      :alt="name || 'Logo association'"
      class="w-full h-full object-contain"
      :width="width"
      :height="width"
      :loading="priority ? 'eager' : 'lazy'"
      :fetchpriority="priority ? 'high' : 'auto'"
    />
    <!-- Fallback : initiale de l'asso sur fond neutre (pas de logo Donoor
         générique qui ferait croire que c'est une asso Donoor). -->
    <span
      v-else
      class="font-bold text-(--color-ink)/40 select-none"
      aria-hidden="true"
      >{{ initial }}</span
    >
  </div>
</template>
